import { state, save } from './state.js';
import { showToast } from './ui.js';
import { apiUrl } from './api';
import type { Lot } from '../types';
import { createLogger } from '../../shared/logger.js';
import { extractErrorMessage } from '../../shared/errorUtils.js';
import { retryWithBackoff } from '../../shared/retry.js';

export interface LotImage {
  canonicalUrl: string;
  displayUrl?: string | null;
}

const DEBUG = /[?&]debug=1(?!d)/i.test(window.location.search || '');
const logImage = createLogger('portal:image');

export const BROKEN_IMAGE_SUPPRESSION_MS = 30 * 60 * 1000; // 30 minutes

type BrokenImageRecord = { count: number; lastFailedAt: number };

const brokenImageFailures: Record<string, BrokenImageRecord> = Object.create(null);

export function stripCacheBustParam(url: string | null | undefined): string | null | undefined {
  if (typeof url !== 'string' || url.indexOf('__bust=') === -1) {
    return url;
  }
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.delete('__bust');
    return parsed.toString();
  } catch {
    let cleaned = url.replace(/([?&])__bust=[^&]*(?:&)?/g, (match, prefix) => {
      if (prefix === '?') {
        return match.charAt(match.length - 1) === '&' ? '?' : '';
      }
      return '';
    });
    if (cleaned.endsWith('?') || cleaned.endsWith('&')) {
      cleaned = cleaned.slice(0, -1);
    }
    return cleaned;
  }
}

export function normalizeImageUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const withoutBust = stripCacheBustParam(trimmed);
  return typeof withoutBust === 'string' && withoutBust.length > 0 ? withoutBust : null;
}

export function getImageProxyUrl(imageUrl: string | null | undefined): string | null | undefined {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return imageUrl;
  }
  try {
    const parsed = new URL(imageUrl, window.location.href);
    const host = (parsed.hostname || '').toLowerCase();
    const isAuctionImage = host.endsWith('hibid.com') || host.endsWith('k-bid.com');
    const path = (parsed.pathname || '').toLowerCase();
    const isHibidAxd = host.endsWith('hibid.com') && path.indexOf('img.axd') !== -1;

    if (isAuctionImage && !isHibidAxd) {
      const encodedUrl = encodeURIComponent(imageUrl);
      return apiUrl('extensions/image-proxy') + '?url=' + encodedUrl;
    }
  } catch {
    // Ignore URL parsing errors
  }
  return imageUrl;
}

export function resolveDisplayImageUrl(url?: string | null): string | null | undefined {
  return getImageProxyUrl(url);
}

export function getBrokenImageKey(lotId: string | null, imageUrl?: string | null): string | null {
  const normalizedUrl = normalizeImageUrl(imageUrl);
  if (!normalizedUrl) {
    return null;
  }
  const normalizedLotId = typeof lotId === 'string' && lotId.trim().length > 0 ? lotId.trim() : '';
  return normalizedLotId ? normalizedLotId + '::' + normalizedUrl : normalizedUrl;
}

export function markImageAsBroken(lotId: string | null, imageUrl?: string | null) {
  const key = getBrokenImageKey(lotId, imageUrl);
  if (!key) {
    return;
  }
  const existing = brokenImageFailures[key];
  const now = Date.now();
  if (existing) {
    existing.count += 1;
    existing.lastFailedAt = now;
  } else {
    brokenImageFailures[key] = { count: 1, lastFailedAt: now };
  }
}

export function isImageMarkedBroken(lotId: string | null, imageUrl?: string | null): boolean {
  const key = getBrokenImageKey(lotId, imageUrl);
  if (!key) {
    return false;
  }
  const record = brokenImageFailures[key];
  if (!record) {
    return false;
  }
  if (record.lastFailedAt && Date.now() - record.lastFailedAt > BROKEN_IMAGE_SUPPRESSION_MS) {
    delete brokenImageFailures[key];
    return false;
  }
  return true;
}

export function collectLotImages(lot: Lot | null | undefined): LotImage[] {
  const result: LotImage[] = [];
  const seen = new Set<string>();
  if (!lot) {
    return result;
  }
  const lotId = lot.id || '';

  function addImage(rawUrl: unknown) {
    const normalized = normalizeImageUrl(rawUrl);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    if (isImageMarkedBroken(lotId, normalized)) {
      return;
    }
    const displayUrl = resolveDisplayImageUrl(normalized);
    result.push({
      canonicalUrl: normalized,
      displayUrl,
    });
  }

  addImage(lot.imageUrl);
  if (Array.isArray(lot.images)) {
    for (let i = 0; i < lot.images.length; i++) {
      addImage(lot.images[i]);
    }
  }
  return result;
}

export function removeLotImage(lotId: string, imageUrl?: string | null): boolean {
  const normalizedUrl = normalizeImageUrl(imageUrl);
  if (normalizedUrl) {
    markImageAsBroken(lotId, normalizedUrl);
  }
  if (!lotId || !normalizedUrl) {
    return false;
  }
  const index = state.lots.findIndex((item: Lot) => item && item.id === lotId);
  if (index < 0) {
    return false;
  }
  const lot = state.lots[index];
  let changed = false;
  const nextImages: string[] = [];
  const seen = new Set<string>();
  if (Array.isArray(lot.images)) {
    for (let i = 0; i < lot.images.length; i++) {
      const normalized = normalizeImageUrl(lot.images[i]);
      if (!normalized) {
        continue;
      }
      if (normalized === normalizedUrl) {
        changed = true;
        continue;
      }
      if (seen.has(normalized)) {
        continue;
      }
      seen.add(normalized);
      nextImages.push(normalized);
    }
  }
  let nextImageUrl = normalizeImageUrl(lot.imageUrl);
  if (nextImageUrl === normalizedUrl) {
    nextImageUrl = nextImages.length > 0 ? nextImages[0] : null;
    changed = true;
  }
  if (!changed) {
    return false;
  }
  const updated = { ...lot } as Lot;
  updated.images = nextImages;
  if (nextImageUrl) {
    updated.imageUrl = nextImageUrl;
  } else {
    delete (updated as { imageUrl?: string }).imageUrl;
  }
  state.lots[index] = updated;
  if (state.mode !== 'auth0' || !lot.remote) {
    save();
  }
  logImage.warn('Removed broken image for lot', { lotId, normalizedUrl });
  showToast('Removed an image that could not be loaded.', { type: 'warning' });
  if (typeof window.__render === 'function') {
    window.__render();
  }
  return true;
}

function __decodeEntities(s: string | null | undefined): string {
  return (s || '').replace(/&amp;/g, '&');
}

function __canonicalizeImageUrl(u: string | null | undefined): string {
  const s = __decodeEntities(u || '');
  try {
    const url = new URL(s, window.location.href);
    url.searchParams.delete('__bust');
    return url.toString();
  } catch (_e) {
    try {
      return s.replace(/([?&])__bust=d+(?=(&|$))/, '$1').replace(/[?&]$/, '');
    } catch {
      logImage.debug('Failed to canonicalize image URL', {
        input: s,
        error: extractErrorMessage(_e),
      });
      return s;
    }
  }
}

export function __getArchive(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem('archivedImages') || '{}');
  } catch {
    return {};
  }
}

export function __saveArchive(map: Record<string, boolean>) {
  try {
    localStorage.setItem('archivedImages', JSON.stringify(map));
  } catch {
    // Ignore storage errors
  }
}

export function __isArchived(u?: string | null): boolean {
  const map = __getArchive();
  const canonical = __canonicalizeImageUrl(u);
  return !!(map[u || ''] || map[canonical]);
}

export function __archiveUrl(u: string | null | undefined) {
  if (!u) return;
  const map = __getArchive();
  const canonical = __canonicalizeImageUrl(u);
  map[canonical] = true;
  map[u] = true;
  try {
    const enc = u.replace(/&/g, '&amp;');
    map[enc] = true;
  } catch {
    // Ignore encoding errors
  }
  __saveArchive(map);
}

export function __pruneHibidArchivedImages() {
  try {
    const map = __getArchive();
    let changed = false;
    Object.keys(map || {}).forEach(key => {
      const lower = key.toLowerCase();
      if (lower.indexOf('hibid.com') !== -1 || lower.indexOf('/extensions/image-proxy') !== -1) {
        delete map[key];
        changed = true;
      }
    });
    if (changed) {
      __saveArchive(map);
    }
  } catch {
    // Ignore errors
  }
}

export async function retryBrokenImageOnce(target?: EventTarget | null): Promise<boolean> {
  try {
    return await retryWithBackoff(
      async () => {
        if (!(target instanceof HTMLImageElement)) {
          return false;
        }
        if (target.dataset && target.dataset.retryAttempted === '1') {
          return false;
        }
        const base = target.getAttribute('data-image-url') || target.currentSrc || target.src || '';
        if (!base) {
          return false;
        }
        if (__isArchived(base)) {
          target.hidden = true;
          return false;
        }
        try {
          const isProxy =
            (target.currentSrc || target.src || '').indexOf('/extensions/image-proxy') !== -1;
          if (isProxy && target.getAttribute('data-image-url')) {
            if (target.dataset) {
              target.dataset.retryAttempted = '1';
            }
            target.removeAttribute('srcset');
            target.src = stripCacheBustParam(target.getAttribute('data-image-url')) || '';
            if (DEBUG) {
              logImage.debug('image error — falling back from proxy to canonical', {
                canonical: target.src,
              });
            }
            return true;
          }
        } catch {
          // Ignore proxy fallback errors
        }
        try {
          const parsed = new URL(base, window.location.href);
          const host = (parsed.hostname || '').toLowerCase();
          const path = (parsed.pathname || '').toLowerCase();
          if (host.endsWith('hibid.com') && path.indexOf('img.axd') !== -1) {
            return false;
          }
        } catch {
          // Ignore URL parse issues
        }
        let busted: string;
        try {
          const u = new URL(base, window.location.href);
          u.searchParams.set('__bust', String(Date.now()));
          busted = u.toString();
        } catch {
          busted = base + (base.indexOf('?') === -1 ? '?' : '&') + '__bust=' + Date.now();
        }
        if (target.dataset) {
          target.dataset.retryAttempted = '1';
        }
        target.src = busted;
        if (DEBUG) {
          logImage.debug('image error — retrying once with cache-bust', { base, busted });
        }
        return true;
      },
      {
        maxAttempts: 0,
        baseDelayMs: 0,
        maxDelayMs: 0,
        backoffMultiplier: 1,
        jitter: false,
      }
    );
  } catch {
    return false;
  }
}

export function handleImageLoadError(event: Event) {
  const target = event?.target;
  if (!(target instanceof HTMLImageElement)) {
    return;
  }
  retryBrokenImageOnce(target)
    .then(didRetry => {
      if (didRetry) {
        return;
      }
      const lotId = target.getAttribute('data-lot-id') || target.getAttribute('data-id') || '';
      const attrUrl = target.getAttribute('data-image-url') || '';
      const fallbackUrl = target.currentSrc || target.src || '';
      try {
        if ((fallbackUrl || '').indexOf('/extensions/image-proxy') !== -1 && attrUrl) {
          target.removeAttribute('srcset');
          target.src = stripCacheBustParam(attrUrl) || attrUrl;
          if (DEBUG) {
            logImage.debug('image error — immediate canonical fallback');
          }
          return;
        }
      } catch {
        // Ignore canonical fallback issues
      }
      if (attrUrl) {
        __archiveUrl(attrUrl);
      }
      if (fallbackUrl) {
        __archiveUrl(fallbackUrl);
      }
      const canon = __canonicalizeImageUrl(attrUrl || fallbackUrl);
      if (canon) {
        __archiveUrl(canon);
      }
      try {
        if (canon) {
          markImageAsBroken(lotId, canon);
        }
      } catch {
        // Ignore mark failures
      }
      try {
        // Hide the broken image element - the CSS will handle displaying a placeholder
        target.removeAttribute('srcset');
        target.hidden = true;
        target.alt = target.alt || 'Image unavailable';
      } catch {
        // Ignore DOM write errors
      }
    })
    .catch(() => {
      // Swallow errors from retry logic to avoid noisy console output for image failures
    });
}
